/* Header user slot: signed-out → "Sign in"; signed-in → avatar + plan badge
 * + trial pill + logout.
 *
 * Subscribes to user state changes (loadUser / login / logout / verify-email)
 * and re-renders in place. Lives next to the lang switcher in the header.
 *
 * Trial pill colors:
 *   - green:  trial with > 3 days left
 *   - amber:  trial with <= 3 days left
 *   - coral:  trial expired or past_due
 *   - matrix: active paid subscription
 *   - hidden: no subscription (status "none")
 *
 * Plan badge tones match the underlying tier (free=ghost, pro=matrix, team=gold).
 */

import { getUser, subscribeUser, trialDaysLeft, logout, setUser, type User } from '../auth';
import { toast } from './toast';

const SLOT_ID = 'header-user-slot';

function avatarInitials(u: User): string {
  const name = u.display_name || u.email;
  const parts = name.replace(/[^a-zA-Z\s]/g, '').split(/\s+/).filter(Boolean);
  if (parts.length >= 2) return (parts[0][0] + parts[1][0]).toUpperCase();
  return name.slice(0, 2).toUpperCase();
}

function trialPillHtml(u: User): string {
  if (u.subscription_status === 'active') {
    return `<span class="trial-pill trial-pill-active" title="Active subscription">
      <span class="trial-dot"></span>${u.subscription_tier.toUpperCase()}
    </span>`;
  }
  if (u.subscription_status === 'trial') {
    const days = trialDaysLeft(u);
    const tone = days != null && days <= 3 ? 'warn' : 'ok';
    return `<a href="/pricing.html" class="trial-pill trial-pill-${tone}" title="Trial — click to upgrade">
      <span class="trial-dot"></span>Trial · ${days}d
    </a>`;
  }
  if (u.subscription_status === 'past_due' || u.subscription_status === 'canceled') {
    return `<a href="/pricing.html" class="trial-pill trial-pill-bad" title="Subscription needs attention">
      <span class="trial-dot"></span>${u.subscription_status === 'past_due' ? 'Past due' : 'Canceled'}
    </a>`;
  }
  // 'none' / 'incomplete' — no pill
  return '';
}

function planBadgeHtml(u: User): string {
  const tone =
    u.subscription_tier === 'team' ? 'team' :
    u.subscription_tier === 'pro'  ? 'pro'  : 'free';
  const label = u.subscription_tier.toUpperCase();
  return `<span class="header-plan-badge header-plan-${tone}" title="${u.subscription_tier} · ${u.subscription_status}">${label}</span>`;
}

async function onLogout(e: Event): Promise<void> {
  e.preventDefault();
  try {
    await logout();
  } catch (err) {
    console.warn('logout failed', err);
  }
  setUser(null);
  toast('Signed out.');
  // Soft redirect to login if the page is gated; for the main app stay put.
}

/** Show the header "Admin" link only for users with role='admin'. The link
 *  is rendered hidden in index.html by default so non-admins never see a flash. */
function syncAdminLink(u: User | null): void {
  const link = document.getElementById('admin-link-header') as HTMLAnchorElement | null;
  if (!link) return;
  link.style.display = u && u.role === 'admin' ? '' : 'none';
}

function render(u: User | null): void {
  syncAdminLink(u);
  const slot = document.getElementById(SLOT_ID);
  if (!slot) return;

  if (!u) {
    slot.innerHTML = `
      <a class="header-signin" href="/login.html"
         data-i18n="auth.signin">Sign in</a>
      <a class="header-cta" href="/register.html"
         data-i18n="auth.register">Start free</a>
    `;
    return;
  }

  slot.innerHTML = `
    ${trialPillHtml(u)}
    <a class="header-user" href="/account.html" title="${u.email}">
      <span class="header-avatar">${avatarInitials(u)}</span>
      <span class="header-user-name">${u.display_name || u.email.split('@')[0]}</span>
      ${planBadgeHtml(u)}
    </a>
    <button class="header-logout-btn" id="header-logout-btn" type="button" title="Log out" aria-label="Log out">
      <svg viewBox="0 0 16 16" width="14" height="14" fill="none" stroke="currentColor" stroke-width="1.7" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
        <path d="M6 14H3a1 1 0 0 1-1-1V3a1 1 0 0 1 1-1h3"/>
        <path d="M10 11l3-3-3-3"/>
        <path d="M13 8H6"/>
      </svg>
    </button>
  `;

  document.getElementById('header-logout-btn')?.addEventListener('click', onLogout);
}

export function attachHeaderUser(): void {
  // Render immediately based on current state (likely null pre-hydration)
  render(getUser());
  // Re-render on every user-state change
  subscribeUser(render);
  // Refresh trial countdown every 60s while the tab is foregrounded so the
  // pill ticks down without a full reload.
  setInterval(() => {
    const u = getUser();
    if (u?.subscription_status === 'trial') render(u);
  }, 60_000);
}
